import { API_BASE_URL } from '@/config/api';
import { getJsonAuthHeaders } from '@/lib/apiHeaders';
import { storage } from '@/services/storage';
import type { 
    EmailTemplate, 
    TemplateCategory, 
    TagStats, 
    TemplateVersion,
    TemplatePermission,
    TemplateVisibility,
    TemplateFilterParams
} from "@/types/WeeklyReport";
import { v4 as uuidv4 } from 'uuid';

// Storage key for email templates
const EMAIL_TEMPLATES_STORAGE_KEY = 'email_templates';

// Helper function to get the current user's email
export const getCurrentUserEmail = () => {
    const userJson = storage.getItem('auth_user');
    if (userJson) {
        const user = JSON.parse(userJson);
        return user.email;
    }
    return null;
};

// Use centralized headers with CSRF protection
const getAuthHeaders = getJsonAuthHeaders;

// Get default BCC email based on environment
export const getDefaultBccEmail = (): string => {
    // Check environment variable first (set in .env.dev as ENV=dev)
    const envFromVar = import.meta.env.VITE_ENV?.toLowerCase();
    if (envFromVar === 'dev' || envFromVar === 'development') {
        return 'igor.mumladze@gmail.com';
    }
    
    // Fallback: Check hostname at runtime
    // Production domain is weekly.develeap.com, so anything else is dev
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isProd = hostname === 'weekly.develeap.com' || hostname.includes('weekly.develeap.com');
    
    // In dev environment, use igor.mumladze@gmail.com for testing
    // In prod, use weekly@develeap.com
    return isProd ? 'weekly@develeap.com' : 'igor.mumladze@gmail.com';
};

// ============================================
// Core Template Operations
// ============================================

// Get all templates for a specific user
const getTemplatesByUser = async (userEmail: string): Promise<EmailTemplate[]> => {
    try {
        console.log(`Fetching email templates for user: ${userEmail}`);
        const response = await fetch(`${API_BASE_URL}/templates/${userEmail}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include', // Required for cookie-based auth
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        const templatesResponse = await response.json();
        const templates = Array.isArray(templatesResponse) ? templatesResponse : [];
        console.log(`Fetched ${templates.length} email templates from API`);

        return templates;
    } catch (error) {
        console.error(`Error fetching email templates for user ${userEmail} from API:`, error);
        return [];
    }
};

// Filter templates with params
const filterTemplates = async (
    userEmail: string, 
    filters: TemplateFilterParams
): Promise<EmailTemplate[]> => {
    try {
        const params = new URLSearchParams();
        if (filters.category) params.append('category', filters.category);
        if (filters.tags && filters.tags.length > 0) params.append('tags', filters.tags.join(','));
        if (filters.search) params.append('search', filters.search);
        
        const url = `${API_BASE_URL}/templates/${userEmail}${params.toString() ? '?' + params.toString() : ''}`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error filtering templates:', error);
        return [];
    }
};

// Get a specific template by ID
const getTemplateById = async (templateId: string): Promise<EmailTemplate | null> => {
    try {
        const response = await fetch(`${API_BASE_URL}/templates/id/${templateId}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting email template by ID:', error);
        return null;
    }
};

// Save a new template or update existing one
const saveTemplate = async (template: EmailTemplate): Promise<EmailTemplate> => {
    try {
        if (!template.createdBy) {
            const userEmail = getCurrentUserEmail();
            if (userEmail) {
                template.createdBy = userEmail;
            } else {
                throw new Error('No user email found, cannot save template');
            }
        }

        const defaultBcc = getDefaultBccEmail();
        if (!template.bcc.includes(defaultBcc)) {
            template.bcc.push(defaultBcc);
        }

        if (!template.id) {
            template.id = uuidv4();
        }

        const now = new Date().toISOString();
        if (!template.createdAt) {
            template.createdAt = now;
        }
        template.updatedAt = now;

        console.log('Saving email template to API');
        const response = await fetch(`${API_BASE_URL}/templates`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include', // Required for cookie-based auth
            body: JSON.stringify(template),
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        console.log('Email template saved to API successfully');
        return await response.json();
    } catch (error) {
        console.error('Error saving email template to API:', error);
        throw error;
    }
};

// Delete a template
const deleteTemplate = async (templateId: string): Promise<boolean> => {
    try {
        const userEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/${templateId}?email=${encodeURIComponent(userEmail || '')}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return true;
    } catch (error) {
        console.error('Error deleting email template:', error);
        return false;
    }
};

// Create a default template for new users
const createDefaultTemplate = async (userEmail: string): Promise<EmailTemplate> => {
    const defaultTemplate: EmailTemplate = {
        id: uuidv4(),
        name: "Default Weekly Report",
        subject: "Develeap Weekly Report",
        to: [],
        cc: [],
        bcc: [getDefaultBccEmail()],
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        visibility: 'private',
        ownerId: userEmail,
        categoryId: null,
        tags: []
    };

    return await saveTemplate(defaultTemplate);
};

// ============================================
// Phase 1: Categories & Tags
// ============================================

const getCategories = async (): Promise<TemplateCategory[]> => {
    try {
        const response = await fetch(`${API_BASE_URL}/templates/categories`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching categories:', error);
        return [];
    }
};

const createCategory = async (
    category: Omit<TemplateCategory, 'id' | 'sortOrder' | 'isSystem'>
): Promise<TemplateCategory | null> => {
    try {
        const response = await fetch(`${API_BASE_URL}/templates/categories`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(category),
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error creating category:', error);
        return null;
    }
};

const updateCategory = async (
    categoryId: string, 
    updates: Partial<TemplateCategory>
): Promise<TemplateCategory | null> => {
    try {
        const response = await fetch(`${API_BASE_URL}/templates/categories/${categoryId}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(updates),
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error updating category:', error);
        return null;
    }
};

const deleteCategory = async (categoryId: string): Promise<boolean> => {
    try {
        const response = await fetch(`${API_BASE_URL}/templates/categories/${categoryId}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return true;
    } catch (error) {
        console.error('Error deleting category:', error);
        return false;
    }
};

const setTemplateCategory = async (templateId: string, categoryId: string | null): Promise<boolean> => {
    try {
        const userEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/${templateId}/category?email=${encodeURIComponent(userEmail || '')}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ categoryId }),
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return true;
    } catch (error) {
        console.error('Error setting template category:', error);
        return false;
    }
};

const getTags = async (): Promise<TagStats[]> => {
    try {
        const response = await fetch(`${API_BASE_URL}/templates/tags`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching tags:', error);
        return [];
    }
};

const updateTemplateTags = async (templateId: string, tags: string[]): Promise<boolean> => {
    try {
        const userEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/${templateId}/tags?email=${encodeURIComponent(userEmail || '')}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ tags }),
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return true;
    } catch (error) {
        console.error('Error updating template tags:', error);
        return false;
    }
};

// ============================================
// Phase 2: Sharing
// ============================================

const shareTemplate = async (
    templateId: string, 
    userEmails: string[], 
    permission: TemplatePermission
): Promise<boolean> => {
    try {
        const ownerEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/${templateId}/share?email=${encodeURIComponent(ownerEmail || '')}`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ userEmails, permission }),
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return true;
    } catch (error) {
        console.error('Error sharing template:', error);
        return false;
    }
};

const revokeShare = async (templateId: string, userId: string): Promise<boolean> => {
    try {
        const ownerEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/${templateId}/share/${encodeURIComponent(userId)}?email=${encodeURIComponent(ownerEmail || '')}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return true;
    } catch (error) {
        console.error('Error revoking share:', error);
        return false;
    }
};

const getSharedWithMe = async (): Promise<EmailTemplate[]> => {
    try {
        const userEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/shared-with-me?email=${encodeURIComponent(userEmail || '')}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching shared templates:', error);
        return [];
    }
};

const getOrganizationTemplates = async (): Promise<EmailTemplate[]> => {
    try {
        const response = await fetch(`${API_BASE_URL}/templates/organization`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching organization templates:', error);
        return [];
    }
};

const changeVisibility = async (
    templateId: string, 
    visibility: TemplateVisibility
): Promise<boolean> => {
    try {
        const userEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/${templateId}/visibility?email=${encodeURIComponent(userEmail || '')}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ visibility }),
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return true;
    } catch (error) {
        console.error('Error changing visibility:', error);
        return false;
    }
};

const copyToPrivate = async (templateId: string): Promise<EmailTemplate | null> => {
    try {
        const userEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/${templateId}/copy?email=${encodeURIComponent(userEmail || '')}`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error copying template:', error);
        return null;
    }
};

// ============================================
// Phase 3: Version Control
// ============================================

const getVersions = async (templateId: string): Promise<TemplateVersion[]> => {
    try {
        const userEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/${templateId}/versions?email=${encodeURIComponent(userEmail || '')}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching versions:', error);
        return [];
    }
};

const getVersion = async (templateId: string, versionId: string): Promise<EmailTemplate | null> => {
    try {
        const userEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/${templateId}/versions/${versionId}?email=${encodeURIComponent(userEmail || '')}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching version:', error);
        return null;
    }
};

const createVersion = async (
    templateId: string, 
    changeDescription?: string
): Promise<TemplateVersion | null> => {
    try {
        const userEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/${templateId}/versions?email=${encodeURIComponent(userEmail || '')}`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ changeDescription }),
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error creating version:', error);
        return null;
    }
};

const revertToVersion = async (templateId: string, versionId: string): Promise<boolean> => {
    try {
        const userEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/${templateId}/revert/${versionId}?email=${encodeURIComponent(userEmail || '')}`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return true;
    } catch (error) {
        console.error('Error reverting to version:', error);
        return false;
    }
};

const getVersionDiff = async (
    templateId: string, 
    v1: number, 
    v2: number
): Promise<Record<string, unknown> | null> => {
    try {
        const userEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/${templateId}/diff/${v1}/${v2}?email=${encodeURIComponent(userEmail || '')}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching diff:', error);
        return null;
    }
};

const pinVersion = async (
    templateId: string, 
    versionId: string, 
    pinned: boolean
): Promise<boolean> => {
    try {
        const userEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/${templateId}/versions/${versionId}/pin?email=${encodeURIComponent(userEmail || '')}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ pinned }),
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return true;
    } catch (error) {
        console.error('Error pinning version:', error);
        return false;
    }
};

const saveDraft = async (
    templateId: string, 
    draft: Partial<EmailTemplate>
): Promise<boolean> => {
    try {
        const userEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/${templateId}/draft?email=${encodeURIComponent(userEmail || '')}`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ draft }),
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return true;
    } catch (error) {
        console.error('Error saving draft:', error);
        return false;
    }
};

const publishDraft = async (templateId: string): Promise<TemplateVersion | null> => {
    try {
        const userEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/${templateId}/draft/publish?email=${encodeURIComponent(userEmail || '')}`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error publishing draft:', error);
        return null;
    }
};

const discardDraft = async (templateId: string): Promise<boolean> => {
    try {
        const userEmail = getCurrentUserEmail();
        const response = await fetch(`${API_BASE_URL}/templates/${templateId}/draft?email=${encodeURIComponent(userEmail || '')}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        return true;
    } catch (error) {
        console.error('Error discarding draft:', error);
        return false;
    }
};

// ============================================
// Export all functions
// ============================================

export const emailTemplateService = {
    // Core operations
    getTemplatesByUser,
    filterTemplates,
    getTemplateById,
    saveTemplate,
    deleteTemplate,
    createDefaultTemplate,
    
    // Categories & Tags
    getCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    setTemplateCategory,
    getTags,
    getAllTags: getTags, // Alias for backwards compatibility
    updateTemplateTags,
    
    // Sharing
    shareTemplate,
    revokeShare,
    getSharedWithMe,
    getTemplatesSharedWithMe: getSharedWithMe, // Alias for backwards compatibility
    getOrganizationTemplates,
    changeVisibility,
    copyToPrivate,
    
    // Version Control
    getVersions,
    getVersion,
    createVersion,
    revertToVersion,
    getVersionDiff,
    pinVersion,
    saveDraft,
    publishDraft,
    discardDraft
};
